import { useForm } from "@formiz/core";
import { getRulebookCard, NotificationType, reportRulebook, useTranslation } from "@next-platform/client-common-core";
import { Rulebook } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { PageLayout, PageSize } from "@/components";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import useToast from "@/hooks/useToast";

import ReportFooter from "./components/ReportFooter";
import ReportForm, { ItemType } from "./components/ReportForm";
import ReportHeader from "./components/ReportHeader";

const INITIAL_VALUES = { userIssueType: "", userReportDetails: "", userExpectedResolution: "" };

const ReportRulebook = () => {
  const reportForm = useForm();
  const { isValid, values } = reportForm;
  const { rulebookId } = useParams();
  const [stateSuccessMessage, setStateSuccessMessage] = useState<string | null>(null);
  const [stateIsFormSubmitted, setStateIsFormSubmitted] = useState(false);
  const [stateIsSubmitAttempted, setStateIsSubmitAttempted] = useState(false);
  const [stateIsSubmitting, setStateIsSubmitting] = useState(false);
  const [stateSubmitError, setStateSubmitError] = useState<string | null>(null);
  const [stateRulebookData, setStateRulebookData] = useState<Rulebook | null>(null);
  const hasFormChanges = useMemo(() => Object.values(values).some((value) => value !== null && value !== undefined && value !== ""), [values]);
  const { t } = useTranslation();
  const { displayToast } = useToast();

  useEffect(() => {
    if (!rulebookId) {
      return;
    }
    getRulebookCard(rulebookId)
      .then((rulebook) => {
        if (rulebook) {
          setStateRulebookData({
            id: rulebook.id,
            name: rulebook.name,
            description: rulebook.description,
            tags: rulebook.tags,
            cover_url: rulebook.cover_url,
            cover_is_nsfw: rulebook.isCoverNsfw,
            creator_username: rulebook.creatorUsername,
            num_meters: rulebook.num_meters,
          } as unknown as Rulebook);
        }
      })
      .catch(() => undefined);
  }, [rulebookId]);

  const handleReportSuccess = useCallback(() => {
    const successMessage = t("lorebook:reportPage.form.successMessage");
    setStateSuccessMessage(successMessage);
    setStateIsFormSubmitted(true);
    displayToast({ message: successMessage, type: NotificationType.success });
  }, [t, displayToast]);

  const submitForm = useCallback(() => {
    setStateIsSubmitAttempted(true);
    if (!isValid || !rulebookId) {
      return;
    }
    // The DTO takes reason codes; the form's single-select issue maps to a one-element list.
    const reasonCodes = reportForm.values.userIssueType ? [String(reportForm.values.userIssueType)] : [];
    setStateIsSubmitting(true);
    setStateSubmitError(null);
    reportRulebook({
      rulebookId,
      reasonCodes,
      ...(reportForm.values.userReportDetails ? { details: String(reportForm.values.userReportDetails) } : {}),
    })
      .then(() => {
        handleReportSuccess();
      })
      .catch(() => {
        setStateSubmitError(t("rulebook:reportPage.form.error.submitFailed"));
      })
      .finally(() => {
        setStateIsSubmitting(false);
      });
  }, [handleReportSuccess, rulebookId, reportForm, isValid, t]);

  return (
    <PageLayout size={PageSize.Medium}>
      <PageStaticMetaTags pageId="report" />
      <ReportHeader itemType={ItemType.Rulebook} />
      <ReportForm
        reportForm={reportForm}
        initialValues={INITIAL_VALUES}
        submitForm={submitForm}
        itemId={rulebookId ?? ""}
        itemType={ItemType.Rulebook}
        isResolutionVisible={false}
        itemData={stateRulebookData}
        isLoading={false}
        isSubmitAttempted={stateIsSubmitAttempted}
        setStateIsSubmitAttempted={setStateIsSubmitAttempted}
      />
      <ReportFooter
        submitForm={submitForm}
        successMessage={stateSuccessMessage}
        isFormSubmitted={stateIsFormSubmitted}
        isValid={isValid}
        hasFormChanges={hasFormChanges}
        isSubmitting={stateIsSubmitting}
        submitError={stateSubmitError}
        itemType={ItemType.Rulebook}
        isSubmitAttempted={stateIsSubmitAttempted}
      />
    </PageLayout>
  );
};

export default ReportRulebook;
